import { proxy } from 'valtio'

import {
    AccountDataSet,
    ApplicationDataSet,
    IDevice,
    MapDataSet,
    IPet,
    RegistrationDataSet,
    IZone, IContact, IEvent, IPoint, IAccount
} from '../interfaces/types.ts'

export const dataState = proxy({
    pets: [] as IPet[],
    devices: [] as IDevice[],
    safeZones: [] as IZone[],
    powerZones: [] as any[],

    account: {} as IAccount,
    billing: [] as any[],
    contacts: [] as IContact[],
    devicePlans: [] as any[],
    paymentMethod: '',

    species: [] as any[],
    breeds: [] as any[],
    timeZones: [] as any[],
    iconColors: [] as any[],
    rates: [] as any[],
    alertTypes: [] as any[],
    plans: [] as any[],
    codes: [] as any[],

    events: [] as IEvent[],
    unreadCount: 0,

    currentPet: null as IPet | null,
    currentDevice: null as IDevice | null,
    currentContact: null as any,
})

// --- Action functions for complex mutations ---

export function setLastLocation(iccid: string, position: IPoint) {
    const device = dataState.devices.find(d => d.iccid === iccid)
    if (device) device.lastLocation = position
}

export function setLastReport(iccid: string, position: IPoint) {
    const device = dataState.devices.find(d => d.iccid === iccid)
    if (device) device.lastReport = position
}

export function updateDeviceStatus(iccid: string, deviceStatus: any) {
    const device = dataState.devices.find(d => d.iccid === iccid)
    if (device) device.status = deviceStatus
}

export function setLastEvent(iccid: string, event: IEvent) {
    const device = dataState.devices.find(d => d.iccid === iccid)
    if (device) device.lastEvent = event
}

export function loadMapData(data: MapDataSet) {
    dataState.pets = data.pets || dataState.pets
    dataState.devices = Array.isArray(data.devices) ? data.devices : dataState.devices
    dataState.safeZones = data.safeZones || dataState.safeZones
    if ((data as any).powerZones) dataState.powerZones = (data as any).powerZones
}

export function loadAccountData(data: AccountDataSet) {
    dataState.account = data.account
    dataState.billing = data.billing
    dataState.contacts = data.contacts
    dataState.devicePlans = data.devicePlans
    dataState.paymentMethod = data.paymentMethod
}

export function loadApplicationData(data: ApplicationDataSet) {
    Object.assign(dataState, data)
    dataState.breeds = Array.isArray(data?.breeds) ? data.breeds : dataState.breeds
    dataState.species = Array.isArray(data?.species) ? data.species : dataState.species
}

export function loadRegistrationData(data: RegistrationDataSet) {
    Object.assign(dataState, data)
    dataState.breeds = Array.isArray(data?.breeds) ? data.breeds : dataState.breeds
    dataState.species = Array.isArray(data?.species) ? data.species : dataState.species
}

export function loadEventData(data: any[]) {
    dataState.events = data
    dataState.unreadCount = data.length
}

export function setProfile(p: any) {
    // Handle nested homeAddress if present (full account object from data API)
    if (p.homeAddress) {
        if (!dataState.account.homeAddress) {
            dataState.account.homeAddress = p.homeAddress
        } else {
            Object.assign(dataState.account.homeAddress, p.homeAddress)
        }
    }

    // Handle flat address fields (from form update API response)
    if (!p.homeAddress && (p.address1 !== undefined || p.city !== undefined)) {
        const addressUpdate: any = {}
        if (p.address1 !== undefined) addressUpdate.address1 = p.address1
        if (p.address2 !== undefined) addressUpdate.address2 = p.address2
        if (p.city !== undefined) addressUpdate.city = p.city
        if (p.state !== undefined) addressUpdate.state = p.state
        if (p.postalCode !== undefined) addressUpdate.postalCode = p.postalCode
        if (p.country !== undefined) addressUpdate.country = p.country

        if (!dataState.account.homeAddress) {
            dataState.account.homeAddress = addressUpdate
        } else {
            Object.assign(dataState.account.homeAddress, addressUpdate)
        }
    }

    // Merge top-level profile fields (exclude address fields that belong in homeAddress)
    const { homeAddress: _ha, address1: _a1, address2: _a2, city: _c, state: _s, postalCode: _p, country: _co, ...rest } = p
    Object.assign(dataState.account, rest)
}

export function setHomeAddress(a: any) {
    if (!dataState.account.homeAddress) {
        dataState.account.homeAddress = a
    } else {
        Object.assign(dataState.account.homeAddress, a)
    }
}

export function resetData() {
    dataState.pets = []
    dataState.devices = []
    dataState.safeZones = []
    dataState.powerZones = []
    dataState.account = {} as IAccount
    dataState.billing = []
    dataState.contacts = []
    dataState.species = []
    dataState.breeds = []
    dataState.timeZones = []
    dataState.iconColors = []
    dataState.rates = []
    dataState.alertTypes = []
    dataState.plans = []
    dataState.devicePlans = []
    dataState.currentPet = null
    dataState.currentDevice = null
}

export default dataState
